/**
 * Slice 15 — Property Inventory setup (VA).
 * Catalog · Count/Level · photo · now · low at · cleaner list handoff.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Loader2 } from "lucide-react";
import { cn } from "@/lib/utils";
import {
  useInventoryItems,
  useCreateInventoryItem,
  useUpdateInventoryItem,
  useUpdateInventoryItemPhoto,
  useDeleteInventoryItem,
  type QuantityType,
} from "@/hooks/useInventory";
import { getInStockCount } from "@/lib/inventoryQuantity";
import {
  INVENTORY_CATALOG,
  INVENTORY_CATALOG_CATEGORIES,
  LEVEL_LOW_OPTIONS,
  LEVEL_NOW_OPTIONS,
  formatLevelLowAt,
  formatLevelNow,
  trackLabel,
  type CatalogCategory,
  type CatalogItem,
  type CatalogTrack,
} from "@/lib/inventoryCatalog";
import { supabase } from "@/integrations/supabase/client";
import { prepareImageFileForUpload } from "@/lib/prepareImageForUpload";
import { toast } from "@/hooks/use-toast";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
} from "@/components/ui/drawer";
import { useIsMobile } from "@/hooks/use-mobile";
import InventoryPredictionBanner from "@/components/owner/InventoryPredictionBanner";
import InventoryPredictionsTable from "@/components/owner/InventoryPredictionsTable";
import LeaveOutRulesDrawer, { initialPlanForItem } from "@/components/owner/LeaveOutRulesDrawer";
import { inventoryToGuideCard, useSaveGuestGuide } from "@/hooks/useGuestGuide";
import { useInventoryLeaveOutRules } from "@/hooks/useInventoryLeaveOutRules";
import { formatLeaveOutRuleSummary } from "@/lib/leaveOut";
import { leaveOutListLines, planHasLeaveOut, type LeaveOutPlan } from "@/lib/leaveOutPlan";

interface InventoryTabProps {
  propertyId: string;
  className?: string;
  focusItemId?: string;
}

type InvItem = {
  id: string;
  item_name: string;
  photo_url?: string | null;
  threshold_count: number | null;
  current_count?: number | null;
  in_stock_qty?: number | null;
  quantity_type?: string | null;
  threshold_value?: number | null;
  current_value?: number | null;
  guest_place?: string | null;
  guest_place_photo_url?: string | null;
  guest_set_qty?: number | null;
  guest_visible?: boolean | null;
  guest_copy?: string | null;
  guest_guide_status?: string | null;
  hospitable_knowledge_item_id?: string | null;
};

function isLevel(item: InvItem): boolean {
  return (item.quantity_type || "").toLowerCase() === "fullness";
}

function isLow(item: InvItem): boolean {
  if (isLevel(item)) {
    if (item.threshold_value == null) return false;
    return (item.current_value ?? 1) <= item.threshold_value;
  }
  if (item.threshold_count == null) return false;
  return getInStockCount(item) <= item.threshold_count;
}

function nowDisplay(item: InvItem): string {
  if (isLevel(item)) return formatLevelNow(item.current_value);
  return String(getInStockCount(item));
}

function extrasSet(item: InvItem): boolean {
  return Boolean(item.guest_place?.trim() || item.guest_place_photo_url);
}

function lowAtDisplay(item: InvItem): string {
  if (isLevel(item)) return formatLevelLowAt(item.threshold_value);
  return item.threshold_count != null ? String(item.threshold_count) : "—";
}

const InventoryTab = ({ propertyId, className, focusItemId }: InventoryTabProps) => {
  const isMobile = useIsMobile();
  const { data: items = [], isLoading } = useInventoryItems(propertyId);
  const createItem = useCreateInventoryItem();
  const updateItem = useUpdateInventoryItem();
  const updatePhoto = useUpdateInventoryItemPhoto();
  const deleteItem = useDeleteInventoryItem();

  const [sheetOpen, setSheetOpen] = useState(false);
  const [catalogOpen, setCatalogOpen] = useState(false);
  const [catalogSearch, setCatalogSearch] = useState("");
  const [catalogCategory, setCatalogCategory] = useState<CatalogCategory | "All">("All");
  const [editingId, setEditingId] = useState<string | null>(null);
  const [name, setName] = useState("");
  const [track, setTrack] = useState<CatalogTrack>("units");
  const [stockCount, setStockCount] = useState(4);
  const [threshold, setThreshold] = useState(2);
  const [levelNow, setLevelNow] = useState(1);
  const [levelLow, setLevelLow] = useState(0.25);
  const [photoUrl, setPhotoUrl] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [editorItemId, setEditorItemId] = useState<string | null>(null);
  const [pickerOpen, setPickerOpen] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);
  const { ruleByItemId, savePlan, isSaving: leaveOutSaving } = useInventoryLeaveOutRules(propertyId);
  const saveGuest = useSaveGuestGuide();

  const list = items as InvItem[];
  const lowItems = useMemo(() => list.filter(isLow), [list]);
  const deleteTarget = list.find((i) => i.id === deleteId) ?? null;

  const itemIsUnset = useCallback(
    (item: InvItem) => {
      const entry = ruleByItemId.get(item.id);
      const plan = entry?.line.leave_out_plan;
      if (plan && planHasLeaveOut(plan)) return false;
      if (entry && !plan) return false;
      return true;
    },
    [ruleByItemId],
  );

  const focusLeaveOutRules = () => {
    setPickerOpen(true);
  };

  const leaveOutLines = (item: InvItem): { lines: string[]; muted: boolean } => {
    const entry = ruleByItemId.get(item.id);
    const plan = entry?.line.leave_out_plan;
    if (plan && planHasLeaveOut(plan)) return { lines: leaveOutListLines(plan), muted: false };
    if (extrasSet(item)) return { lines: ["Extras set · leave-out not set"], muted: true };
    if (entry?.rule) return { lines: [formatLeaveOutRuleSummary(entry.rule)], muted: false };
    return { lines: [], muted: true };
  };

  const unsetItems = list.filter(itemIsUnset);
  const editorItem = list.find((i) => i.id === editorItemId) ?? null;
  const pickerItems = unsetItems.length > 0 ? unsetItems : list;

  const uploadPlacePhoto = async (file: File): Promise<string | null> => {
    try {
      const prepared = await prepareImageFileForUpload(file);
      const path = `${propertyId}/leave-out/${crypto.randomUUID()}.jpg`;
      const { error } = await supabase.storage.from("reference-photos").upload(path, prepared, {
        contentType: "image/jpeg",
        upsert: false,
      });
      if (error) throw error;
      const { data } = supabase.storage.from("reference-photos").getPublicUrl(path);
      return data.publicUrl;
    } catch (err) {
      toast({
        title: "Upload failed",
        description: err instanceof Error ? err.message : "Try again",
        variant: "destructive",
      });
      return null;
    }
  };

  const saveEditor = async (plan: LeaveOutPlan) => {
    if (!editorItem) return;
    const ok = await savePlan(editorItem.id, editorItem.item_name, plan);
    if (ok) setEditorItemId(null);
  };

  const filteredCatalog = useMemo(() => {
    const q = catalogSearch.trim().toLowerCase();
    return INVENTORY_CATALOG.filter((row) => {
      if (catalogCategory !== "All" && row.category !== catalogCategory) return false;
      if (q && !row.name.toLowerCase().includes(q)) return false;
      return true;
    });
  }, [catalogSearch, catalogCategory]);

  const catalogGrouped = useMemo(() => {
    const order = INVENTORY_CATALOG_CATEGORIES.filter((c) => c !== "All") as CatalogCategory[];
    return order
      .map((cat) => ({
        cat,
        rows: filteredCatalog.filter((r) => r.category === cat),
      }))
      .filter((g) => g.rows.length > 0);
  }, [filteredCatalog]);

  useEffect(() => {
    if (!focusItemId || list.length === 0) return;
    const found = list.find((i) => i.id === focusItemId);
    if (found) openEdit(found);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focusItemId, list]);

  const resetSheet = () => {
    setEditingId(null);
    setName("");
    setTrack("units");
    setStockCount(4);
    setThreshold(2);
    setLevelNow(1);
    setLevelLow(0.25);
    setPhotoUrl(null);
  };

  const openCatalog = () => {
    setCatalogSearch("");
    setCatalogCategory("All");
    setCatalogOpen(true);
  };

  const openCustom = () => {
    setCatalogOpen(false);
    resetSheet();
    setSheetOpen(true);
  };

  const openFromCatalog = (row: CatalogItem) => {
    setCatalogOpen(false);
    resetSheet();
    setName(row.name);
    setTrack(row.track);
    if (row.track === "fullness") {
      setLevelNow(1);
      setLevelLow(0.25);
    } else {
      setStockCount(4);
      setThreshold(2);
    }
    setSheetOpen(true);
  };

  const openEdit = (item: InvItem) => {
    setEditingId(item.id);
    setName(item.item_name || "");
    const level = isLevel(item);
    setTrack(level ? "fullness" : "units");
    setStockCount(getInStockCount(item));
    setThreshold(item.threshold_count ?? 2);
    setLevelNow(item.current_value ?? 1);
    setLevelLow(item.threshold_value ?? 0.25);
    setPhotoUrl(item.photo_url ?? null);
    setSheetOpen(true);
  };

  const closeSheet = () => {
    setSheetOpen(false);
    resetSheet();
  };

  const uploadPhoto = async (file: File) => {
    setBusy(true);
    try {
      const prepared = await prepareImageFileForUpload(file);
      const path = `${propertyId}/inventory/${crypto.randomUUID()}.jpg`;
      const { error } = await supabase.storage.from("reference-photos").upload(path, prepared, {
        contentType: "image/jpeg",
        upsert: false,
      });
      if (error) throw error;
      const { data } = supabase.storage.from("reference-photos").getPublicUrl(path);
      setPhotoUrl(data.publicUrl);
      if (editingId) {
        await updatePhoto.mutateAsync({ id: editingId, propertyId, photoUrl: data.publicUrl });
      }
    } catch (err) {
      toast({
        title: "Upload failed",
        description: err instanceof Error ? err.message : "Try again",
        variant: "destructive",
      });
    } finally {
      setBusy(false);
    }
  };

  const handleSave = async () => {
    const trimmed = name.trim();
    if (!trimmed) {
      toast({ title: "Name required", variant: "destructive" });
      return;
    }
    setBusy(true);
    try {
      const quantityType: QuantityType = track === "fullness" ? "fullness" : "units";
      const payload = {
        propertyId,
        itemName: trimmed,
        photoUrl,
        quantityType,
        inventoryScope: "supply_closet" as const,
        thresholdCount: quantityType === "units" ? threshold : null,
        currentCount: quantityType === "units" ? stockCount : 0,
        currentValue: quantityType === "fullness" ? levelNow : null,
        thresholdValue: quantityType === "fullness" ? levelLow : null,
      };
      if (editingId) {
        await updateItem.mutateAsync({ id: editingId, ...payload });
      } else {
        await createItem.mutateAsync(payload);
      }
      toast({ title: "Saved" });
      closeSheet();
    } catch (err) {
      toast({
        title: "Could not save",
        description: err instanceof Error ? err.message : "Try again",
        variant: "destructive",
      });
    } finally {
      setBusy(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    setBusy(true);
    try {
      await deleteItem.mutateAsync({ id: deleteId, propertyId });
      toast({ title: "Deleted" });
      setDeleteId(null);
      if (editingId === deleteId) closeSheet();
    } catch (err) {
      toast({
        title: "Could not delete",
        description: err instanceof Error ? err.message : "Try again",
        variant: "destructive",
      });
    } finally {
      setBusy(false);
    }
  };

  const sheetTitle = editingId ? "Edit item" : "Add item";

  const sheetBody = (
    <div className="flex flex-col gap-5">
      <input
        ref={fileRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={(e) => {
          const f = e.target.files?.[0];
          e.target.value = "";
          if (f) void uploadPhoto(f);
        }}
      />

      <button
        type="button"
        disabled={busy}
        onClick={() => fileRef.current?.click()}
        className={cn(
          "relative flex h-[150px] flex-col items-center justify-center gap-2.5 overflow-hidden rounded-[18px]",
          photoUrl
            ? "border-[1.5px] border-[#e6edf1] bg-[#eff4f7]"
            : "border-2 border-dashed border-[#cfdbe3] bg-[#f4f7f9] hover:bg-[#eef3f6]",
        )}
      >
        {photoUrl ? (
          <>
            <img src={photoUrl} alt="" className="absolute inset-0 h-full w-full object-cover" />
            <div className="absolute bottom-3 right-3 z-10 rounded-[10px] border-[1.5px] border-[#e6edf1] bg-white px-3.5 py-1.5 text-[13px] font-bold text-[#475569]">
              Retake
            </div>
          </>
        ) : (
          <>
            <svg width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M4 8.5A2.5 2.5 0 016.5 6h1.2l1.1-2h6.4l1.1 2h1.2A2.5 2.5 0 0120 8.5v8A2.5 2.5 0 0117.5 19h-11A2.5 2.5 0 014 16.5z" />
              <circle cx="12" cy="12.5" r="3.4" />
            </svg>
            <div className="text-[16px] font-bold text-[#475569]">Add photo</div>
          </>
        )}
      </button>

      <div className="flex flex-col gap-[7px]">
        <div className="text-xs font-bold tracking-[0.5px] text-[#64748b]">NAME</div>
        <input
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Dishwasher pods"
          className="h-[52px] rounded-[14px] border-[1.5px] border-[#d7e0e6] px-[15px] text-[17px] font-medium outline-none focus:border-[#24a89a]"
        />
      </div>

      <div className="flex flex-col gap-[9px]">
        <div className="text-xs font-bold tracking-[0.5px] text-[#64748b]">TRACK</div>
        <div className="flex gap-2.5">
          {(
            [
              { id: "units" as const, title: "Count", sub: "Boxes, plates, towels" },
              { id: "fullness" as const, title: "Level", sub: "Bottles, rolls, packs" },
            ] as const
          ).map((opt) => {
            const on = track === opt.id;
            return (
              <button
                key={opt.id}
                type="button"
                onClick={() => setTrack(opt.id)}
                className={cn(
                  "flex flex-1 flex-col gap-0.5 rounded-[16px] border-[1.5px] p-[15px] text-left",
                  on
                    ? "border-[#24a89a] bg-[#24a89a] text-white"
                    : "border-[#e6edf1] bg-white text-[#0f1720] hover:bg-[#f4f7f9]",
                )}
              >
                <div className="text-[19px] font-bold">{opt.title}</div>
                <div className={cn("text-[13px] font-medium", on ? "text-white/90" : "text-[#64748b]")}>
                  {opt.sub}
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {track === "units" ? (
        <>
          <div className="flex flex-col gap-[9px]">
            <div className="text-xs font-bold tracking-[0.5px] text-[#64748b]">NOW</div>
            <div className="flex items-center gap-3.5">
              <button
                type="button"
                onClick={() => setStockCount((n) => Math.max(0, n - 1))}
                className="flex h-[58px] w-[58px] items-center justify-center rounded-[16px] border-[1.5px] border-[#e6edf1] bg-[#f4f7f9] text-[26px] font-bold text-[#475569]"
              >
                −
              </button>
              <div className="min-w-[54px] text-center text-[30px] font-bold tabular-nums">{stockCount}</div>
              <button
                type="button"
                onClick={() => setStockCount((n) => n + 1)}
                className="flex h-[58px] w-[58px] items-center justify-center rounded-[16px] border-[1.5px] border-[#e6edf1] bg-[#f4f7f9] text-[26px] font-bold text-[#475569]"
              >
                +
              </button>
              <div className="text-[14px] font-medium text-[#94a3b8]">in the unit today</div>
            </div>
          </div>
          <div className="flex flex-col gap-[9px]">
            <div className="text-xs font-bold tracking-[0.5px] text-[#64748b]">LOW AT</div>
            <div className="flex items-center gap-3.5">
              <button
                type="button"
                onClick={() => setThreshold((n) => Math.max(0, n - 1))}
                className="flex h-[58px] w-[58px] items-center justify-center rounded-[16px] border-[1.5px] border-[#e6edf1] bg-[#f4f7f9] text-[26px] font-bold text-[#475569]"
              >
                −
              </button>
              <div className="min-w-[54px] text-center text-[30px] font-bold tabular-nums">{threshold}</div>
              <button
                type="button"
                onClick={() => setThreshold((n) => n + 1)}
                className="flex h-[58px] w-[58px] items-center justify-center rounded-[16px] border-[1.5px] border-[#e6edf1] bg-[#f4f7f9] text-[26px] font-bold text-[#475569]"
              >
                +
              </button>
              <div className="text-[14px] font-medium text-[#94a3b8]">flag Low at {threshold} or fewer</div>
            </div>
          </div>
        </>
      ) : (
        <>
          <div className="flex flex-col gap-[9px]">
            <div className="text-xs font-bold tracking-[0.5px] text-[#64748b]">NOW</div>
            <div className="flex gap-1.5">
              {LEVEL_NOW_OPTIONS.map((opt) => {
                const on = levelNow === opt.value;
                return (
                  <button
                    key={opt.label}
                    type="button"
                    onClick={() => setLevelNow(opt.value)}
                    className={cn(
                      "flex h-14 flex-1 items-center justify-center rounded-[14px] border-[1.5px] text-[16px] font-bold",
                      on
                        ? "border-[#24a89a] bg-[#24a89a] text-white"
                        : "border-[#e6edf1] bg-white text-[#0f1720] hover:bg-[#f4f7f9]",
                    )}
                  >
                    {opt.label}
                  </button>
                );
              })}
            </div>
          </div>
          <div className="flex flex-col gap-[9px]">
            <div className="text-xs font-bold tracking-[0.5px] text-[#64748b]">LOW AT</div>
            <div className="flex gap-1.5">
              {LEVEL_LOW_OPTIONS.map((opt) => {
                const on = levelLow === opt.value;
                return (
                  <button
                    key={opt.label}
                    type="button"
                    onClick={() => setLevelLow(opt.value)}
                    className={cn(
                      "flex h-[52px] flex-1 items-center justify-center rounded-[14px] border-[1.5px] text-[15px] font-bold",
                      on
                        ? "border-[#b45309] bg-[#b45309] text-white"
                        : "border-[#e6edf1] bg-white text-[#0f1720] hover:bg-[#f4f7f9]",
                    )}
                  >
                    {opt.label}
                  </button>
                );
              })}
            </div>
          </div>
        </>
      )}

      <button
        type="button"
        disabled={busy}
        onClick={() => void handleSave()}
        className="flex h-[58px] w-full items-center justify-center rounded-[16px] bg-[#24a89a] text-[19px] font-bold text-white hover:bg-[#1f958a]"
      >
        {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : "Save"}
      </button>

      {editingId && (
        <button
          type="button"
          onClick={() => {
            setDeleteId(editingId);
          }}
          className="text-center text-[15px] font-bold text-[#b4453a] hover:text-[#8f3529]"
        >
          Delete
        </button>
      )}
    </div>
  );

  const catalogBody = (
    <div className="flex max-h-[min(80vh,640px)] flex-col overflow-hidden">
      <div className="flex flex-col gap-4 border-b-[1.5px] border-[#f1f5f8] px-1 pb-4">
        <div className="flex h-[50px] items-center gap-2.5 rounded-[14px] border-[1.5px] border-[#e6edf1] bg-[#f4f7f9] px-[15px]">
          <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="2.4" strokeLinecap="round">
            <circle cx="11" cy="11" r="6.5" />
            <path d="M16 16l4 4" />
          </svg>
          <input
            value={catalogSearch}
            onChange={(e) => setCatalogSearch(e.target.value)}
            placeholder="Search"
            className="h-full flex-1 bg-transparent text-[16px] font-medium outline-none placeholder:text-[#94a3b8]"
          />
        </div>
        <div className="flex flex-wrap gap-[7px]">
          {INVENTORY_CATALOG_CATEGORIES.map((cat) => {
            const on = catalogCategory === cat;
            return (
              <button
                key={cat}
                type="button"
                onClick={() => setCatalogCategory(cat)}
                className={cn(
                  "rounded-full px-[15px] py-2 text-[13px] font-bold",
                  on
                    ? "bg-[#24a89a] text-white"
                    : "border-[1.5px] border-[#e6edf1] bg-[#f4f7f9] text-[#475569] hover:bg-[#eef3f6]",
                )}
              >
                {cat}
              </button>
            );
          })}
        </div>
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto">
        {catalogGrouped.length === 0 ? (
          <div className="px-2 py-10 text-center text-[15px] font-medium text-[#64748b]">No matches</div>
        ) : (
          catalogGrouped.map((g) => (
            <div key={g.cat}>
              <div className="border-b-[1.5px] border-[#f1f5f8] bg-[#fafcfd] px-2 py-3 text-[11px] font-bold tracking-[1px] text-[#8494a2]">
                {g.cat.toUpperCase()}
              </div>
              {g.rows.map((row) => (
                <button
                  key={`${row.category}-${row.name}`}
                  type="button"
                  onClick={() => openFromCatalog(row)}
                  className="flex w-full items-center gap-3 border-b-[1.5px] border-[#f6f9fa] px-2 py-[11px] text-left hover:bg-[#f4f7f9]"
                >
                  <div className="h-10 w-10 shrink-0 rounded-[11px] border-[1.5px] border-[#e6edf1] bg-[repeating-linear-gradient(135deg,#e6edf1_0_6px,#eff4f7_6px_12px)]" />
                  <div className="min-w-0 flex-1 text-[16px] font-bold tracking-[-0.2px] text-[#0f1720]">
                    {row.name}
                  </div>
                  <div className="text-[13px] font-medium text-[#94a3b8]">{trackLabel(row.track)}</div>
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#24a89a" strokeWidth="2.8" strokeLinecap="round">
                    <path d="M12 5v14M5 12h14" />
                  </svg>
                </button>
              ))}
            </div>
          ))
        )}
      </div>

      <div className="flex items-center justify-between gap-3 border-t-[1.5px] border-[#e6edf1] bg-white pt-4">
        <div className="text-[13px] font-medium text-[#64748b]">Not in the list?</div>
        <button
          type="button"
          onClick={openCustom}
          className="flex h-12 items-center rounded-[14px] border-[1.5px] border-[#24a89a] bg-white px-6 text-[16px] font-bold text-[#15776d] hover:bg-[#f0f8f7]"
        >
          Create custom
        </button>
      </div>
    </div>
  );

  return (
    <div className={cn("flex min-w-0 flex-col gap-5", className)} data-tour="tour-inventory-slice15">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div className="flex flex-col gap-1">
          <h2 className="sr-only">
            Inventory
          </h2>
          <p className="text-[15px] font-medium text-[#64748b]">What cleaners count</p>
        </div>
        <div className="hidden items-center gap-3 sm:flex">
          <button
            type="button"
            onClick={openCatalog}
            className="flex h-[46px] items-center rounded-[13px] bg-[#24a89a] px-[22px] text-base font-bold text-white hover:bg-[#1f958a]"
          >
            + Item
          </button>
        </div>
      </div>

      {lowItems.length > 0 ? (
        <div className="flex items-center gap-2.5 rounded-[14px] bg-[#fdeee1] px-[18px] py-[13px] text-[15px] font-bold text-[#96501d]">
          <div className="h-[9px] w-[9px] rounded-full bg-[#d97a2b]" />
          {lowItems.length} low
          {lowItems.length <= 3
            ? ` · ${lowItems.map((i) => i.item_name).join(" · ")}`
            : ` · ${lowItems
                .slice(0, 3)
                .map((i) => i.item_name)
                .join(" · ")}…`}
        </div>
      ) : null}

      <InventoryPredictionBanner propertyId={propertyId} />
      <InventoryPredictionsTable
        propertyId={propertyId}
        onGoToLeaveOutRules={focusLeaveOutRules}
        unsetCount={unsetItems.length}
        allItemsSet={list.length > 0 && unsetItems.length === 0}
      />

      {isLoading ? (
        <div className="flex justify-center py-16 text-[#64748b]">
          <Loader2 className="h-8 w-8 animate-spin" />
        </div>
      ) : list.length === 0 ? (
        <div className="flex min-h-[340px] flex-col items-center justify-center gap-[22px] rounded-[18px] border-[1.5px] border-[#e6edf1] bg-white px-6 py-16">
          <div className="flex h-[74px] w-[74px] items-center justify-center rounded-[22px] border-[1.5px] border-[#e6edf1] bg-[#f4f7f9]">
            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <rect x="4" y="7" width="16" height="13" rx="2" />
              <path d="M4 11h16M9 7V4h6v3" />
            </svg>
          </div>
          <div className="flex flex-col items-center gap-[7px]">
            <div className="text-[24px] font-bold tracking-[-0.4px] text-[#0f1720]">No items yet</div>
            <div className="text-[16px] font-medium text-[#64748b]">Add what cleaners restock</div>
          </div>
          <div className="flex flex-col items-center gap-3">
            <button
              type="button"
              onClick={openCatalog}
              className="flex h-[58px] items-center rounded-[16px] bg-[#24a89a] px-[34px] text-[19px] font-bold text-white hover:bg-[#1f958a]"
            >
              Browse catalog
            </button>
            <button
              type="button"
              onClick={openCustom}
              className="text-[15px] font-bold text-[#15776d] hover:text-[#0f5c54]"
            >
              Create custom
            </button>
          </div>
        </div>
      ) : (
        <>
          <div
            id="inventory-items"
            className="hidden scroll-mt-4 overflow-hidden rounded-[18px] border-[1.5px] border-[#e6edf1] bg-white md:block"
          >
            <div className="grid grid-cols-[1.7fr_120px_140px_160px_minmax(120px,1fr)_40px] gap-4 border-b-[1.5px] border-[#e6edf1] bg-[#f4f7f9] px-[22px] py-3.5 text-xs font-bold tracking-[0.5px] text-[#64748b]">
              <div>ITEM</div>
              <div>TRACK</div>
              <div>NOW</div>
              <div>LOW AT</div>
              <div>LEAVE OUT</div>
              <div />
            </div>
            {list.map((item) => {
              const low = isLow(item);
              const { lines, muted } = leaveOutLines(item);
              return (
                <div key={item.id} className="border-b-[1.5px] border-[#f1f5f8] last:border-b-0">
                  <button
                    type="button"
                    onClick={() => setEditorItemId(item.id)}
                    className="grid w-full grid-cols-[1.7fr_120px_140px_160px_minmax(120px,1fr)_40px] items-center gap-4 px-[22px] py-3.5 text-left hover:bg-[#fafcfd]"
                  >
                    <div className="flex min-w-0 items-center gap-3">
                      <div className="flex h-11 w-11 shrink-0 overflow-hidden rounded-[11px] border-[1.5px] border-[#e6edf1] bg-[#eff4f7]">
                        {item.photo_url ? (
                          <img src={item.photo_url} alt="" className="h-full w-full object-cover" />
                        ) : null}
                      </div>
                      <div className="min-w-0">
                        <div className="truncate text-base font-bold tracking-[-0.2px] text-[#0f1720]">
                          {item.item_name}
                        </div>
                        {low ? (
                          <span className="text-xs font-bold text-[#96501d]">Low</span>
                        ) : null}
                      </div>
                    </div>
                    <div>
                      <span className="inline-flex rounded-full border-[1.5px] border-[#e6edf1] bg-[#f4f7f9] px-3 py-[5px] text-xs font-bold text-[#475569]">
                        {isLevel(item) ? "Level" : "Count"}
                      </span>
                    </div>
                    <div className="text-lg font-bold text-[#0f1720]">{nowDisplay(item)}</div>
                    <div className="text-[15px] font-medium text-[#64748b]">{lowAtDisplay(item)}</div>
                    <div className="min-w-0">
                      {lines.length > 0 && !muted ? (
                        <div className="flex flex-col gap-0.5">
                          <div className="truncate text-[14.5px] font-bold text-[#0f1720]">{lines[0]}</div>
                          {lines[1] ? (
                            <div className="truncate text-[13px] font-medium text-[#8494a2]">{lines[1]}</div>
                          ) : null}
                        </div>
                      ) : (
                        <span className="text-[14.5px] font-medium text-[#94a3b0]">
                          {lines[0] || "Not set"}
                        </span>
                      )}
                    </div>
                    <div />
                  </button>
                </div>
              );
            })}
          </div>

          <div id="inventory-items-mobile" className="flex scroll-mt-4 flex-col gap-2.5 md:hidden">
            {list.map((item) => {
              const low = isLow(item);
              const { lines } = leaveOutLines(item);
              return (
                <button
                  key={item.id}
                  type="button"
                  onClick={() => setEditorItemId(item.id)}
                  className={cn(
                    "flex w-full items-center gap-3 rounded-[15px] border-[1.5px] px-3.5 py-3 text-left",
                    low ? "border-[#f0d2b4] bg-[#fff8f1]" : "border-[#e6edf1] bg-white",
                  )}
                >
                  <div className="flex h-11 w-11 shrink-0 overflow-hidden rounded-xl border-[1.5px] border-[#e6edf1] bg-[#eff4f7]">
                    {item.photo_url ? (
                      <img src={item.photo_url} alt="" className="h-full w-full object-cover" />
                    ) : null}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-base font-bold tracking-[-0.2px] text-[#0f1720]">
                      {item.item_name}
                    </div>
                    <div className="text-sm font-medium text-[#64748b]">
                      {isLevel(item) ? "Level" : "Count"} · {nowDisplay(item)}
                      {` · ${lines[0] || "Not set"}`}
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        </>
      )}

      {list.length > 0 && (
        <div className="flex flex-col gap-2 sm:hidden">
          <button
            type="button"
            onClick={openCatalog}
            className="flex h-[58px] w-full items-center justify-center rounded-2xl bg-[#24a89a] text-[17px] font-bold text-white hover:bg-[#1f958a]"
          >
            + Item
          </button>
        </div>
      )}

      {isMobile ? (
        <>
          <Drawer open={catalogOpen} onOpenChange={setCatalogOpen}>
            <DrawerContent className="rounded-t-[24px] px-5 pb-7 pt-3">
              <div className="mx-auto mb-3 h-1.5 w-11 rounded-full bg-[#e6edf1]" />
              <DrawerHeader className="p-0 pb-3 text-left">
                <DrawerTitle className="text-[22px] font-bold tracking-[-0.3px]">Add items</DrawerTitle>
              </DrawerHeader>
              {catalogBody}
            </DrawerContent>
          </Drawer>
          <Drawer open={sheetOpen} onOpenChange={(o) => !o && closeSheet()}>
            <DrawerContent className="max-h-[92vh] rounded-t-[24px] px-5 pb-7 pt-3">
              <div className="mx-auto mb-3 h-1.5 w-11 rounded-full bg-[#e6edf1]" />
              <DrawerHeader className="p-0 pb-2 text-left">
                <DrawerTitle className="text-[22px] font-bold tracking-[-0.3px]">{sheetTitle}</DrawerTitle>
              </DrawerHeader>
              <div className="overflow-y-auto">{sheetBody}</div>
            </DrawerContent>
          </Drawer>
        </>
      ) : (
        <>
          <Dialog open={catalogOpen} onOpenChange={setCatalogOpen}>
            <DialogContent className="gap-0 overflow-hidden rounded-[22px] p-6 sm:max-w-[560px]">
              <DialogHeader className="pb-2">
                <DialogTitle className="text-[23px] font-bold tracking-[-0.3px]">Add items</DialogTitle>
              </DialogHeader>
              {catalogBody}
            </DialogContent>
          </Dialog>
          <Dialog open={sheetOpen} onOpenChange={(o) => !o && closeSheet()}>
            <DialogContent className="max-h-[90vh] gap-0 overflow-y-auto rounded-[22px] p-6 sm:max-w-[470px]">
              <DialogHeader>
                <DialogTitle className="text-[23px] font-bold tracking-[-0.3px]">{sheetTitle}</DialogTitle>
              </DialogHeader>
              <div className="pt-4">{sheetBody}</div>
            </DialogContent>
          </Dialog>
        </>
      )}

      {pickerOpen ? (
        <div className="fixed inset-0 z-50">
          <button
            type="button"
            aria-label="Close"
            className="absolute inset-0 bg-[rgba(15,23,32,0.28)]"
            onClick={() => setPickerOpen(false)}
          />
          <div className="absolute right-6 top-36 flex w-[min(400px,calc(100vw-32px))] flex-col overflow-hidden rounded-2xl bg-white shadow-[0_24px_60px_rgba(15,23,32,0.22)] max-sm:left-4 max-sm:right-4 max-sm:top-24">
            <div className="flex flex-col gap-0.5 px-5 pb-2.5 pt-4">
              <div className="text-lg font-bold tracking-[-0.2px] text-[#0f1720]">Which item?</div>
              <div className="text-[13px] font-medium text-[#8494a2]">
                {unsetItems.length > 0 ? "Not set yet" : "All items"}
              </div>
            </div>
            {pickerItems.map((item) => (
              <button
                key={item.id}
                type="button"
                onClick={() => {
                  setPickerOpen(false);
                  setEditorItemId(item.id);
                }}
                className="flex min-h-[60px] items-center gap-3.5 border-t border-[#eef2f5] px-5 py-2.5 text-left hover:bg-[#f4f7f9]"
              >
                <div className="h-10 w-10 shrink-0 overflow-hidden rounded-[10px] bg-[#eff4f7]">
                  {item.photo_url ? <img src={item.photo_url} alt="" className="h-full w-full object-cover" /> : null}
                </div>
                <div className="min-w-0 flex-1 truncate text-[15.5px] font-bold text-[#0f1720]">
                  {item.item_name}
                </div>
                <div className="shrink-0 text-[13.5px] font-medium text-[#8494a2]">Now {nowDisplay(item)}</div>
                <div className="text-lg font-medium text-[#94a3b0]">›</div>
              </button>
            ))}
          </div>
        </div>
      ) : null}

      {editorItem ? (
        <div className="fixed inset-0 z-50">
          <button
            type="button"
            aria-label="Close"
            className="absolute inset-0 bg-[rgba(15,23,32,0.28)]"
            onClick={() => setEditorItemId(null)}
          />
          <div className="absolute inset-y-0 right-0 flex w-[620px] max-w-full flex-col bg-white shadow-[-18px_0_50px_rgba(15,23,32,0.16)] max-md:inset-0 max-md:w-full">
            <LeaveOutRulesDrawer
              key={editorItem.id}
              mobile={isMobile}
              itemName={editorItem.item_name}
              itemPhoto={editorItem.photo_url}
              nowLabel={nowDisplay(editorItem)}
              lowLabel={lowAtDisplay(editorItem)}
              initialPlan={initialPlanForItem(ruleByItemId.get(editorItem.id)?.line.leave_out_plan)}
              extrasPlace={editorItem.guest_place?.trim() || ""}
              extrasPhoto={editorItem.guest_place_photo_url || null}
              saving={leaveOutSaving}
              savingExtras={saveGuest.isPending}
              onCancel={() => setEditorItemId(null)}
              onSave={(plan) => void saveEditor(plan)}
              onSaveExtras={async (place, photoUrl) => {
                try {
                  await saveGuest.mutateAsync({
                    propertyId,
                    card: inventoryToGuideCard(editorItem as unknown as Record<string, unknown>, ""),
                    visible: true,
                    qty: null,
                    place,
                    placePhotoUrl: photoUrl,
                  });
                } catch (e) {
                  toast({
                    title: "Couldn't save extras",
                    description: e instanceof Error ? e.message : undefined,
                    variant: "destructive",
                  });
                  throw e;
                }
              }}
              onEditStock={() => {
                const item = editorItem;
                setEditorItemId(null);
                openEdit(item);
              }}
              onUploadPhoto={uploadPlacePhoto}
            />
          </div>
        </div>
      ) : null}

      <Dialog open={!!deleteId} onOpenChange={(o) => !o && setDeleteId(null)}>
        <DialogContent className="rounded-[20px] p-6 sm:max-w-[400px]">
          <DialogHeader>
            <DialogTitle className="text-[22px] font-bold tracking-[-0.3px]">Delete item?</DialogTitle>
          </DialogHeader>
          <p className="text-[15px] font-medium leading-relaxed text-[#64748b]">
            {deleteTarget?.item_name || "This item"} stops showing in the cleaner&apos;s Inventory step. Past
            turnovers keep their values.
          </p>
          <DialogFooter className="gap-2.5 sm:justify-end">
            <button
              type="button"
              onClick={() => setDeleteId(null)}
              className="flex h-[54px] flex-1 items-center justify-center rounded-[15px] border-[1.5px] border-[#e6edf1] bg-[#f4f7f9] text-[17px] font-bold text-[#475569] sm:flex-none sm:px-5"
            >
              Cancel
            </button>
            <button
              type="button"
              disabled={busy}
              onClick={() => void handleDelete()}
              className="flex h-[54px] flex-1 items-center justify-center rounded-[15px] bg-[#b4453a] text-[17px] font-bold text-white hover:bg-[#9a3a30] sm:flex-none sm:px-6"
            >
              {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : "Delete"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default InventoryTab;
